/* The Backflow Depot - hero instant search (2026-10-09).
   Type-ahead for the homepage hero search box. Pairs with snippets/bfd-instant-search.liquid.

   - Brands and category shortcuts come from the hero section settings (inline JSON).
   - Models come from /collections?view=bfd-search-index (every collection on the "model"
     template), fetched once on first focus and kept for the browser session.
   - Products come from Shopify predictive search (/search/suggest.json), debounced.
   - Enter on an exact brand, model or shortcut goes straight to that page; anything else
     runs the normal store search. With no JavaScript the form simply submits to /search.

   The matching code at the top is pure (no DOM) so it can be tested in Node:
     node test/render-test.mjs                                                         */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.BfdInstantSearch = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ---------- pure matching ---------- */

  function norm(s) {
    return String(s == null ? '' : s).toLowerCase()
      .replace(/&/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
  }
  function compact(s) { return norm(s).replace(/ /g, ''); }
  function words(s) { var n = norm(s); return n ? n.split(' ') : []; }
  function hasDigit(w) { return /\d/.test(w); }

  // Levenshtein distance with an early exit once it passes max.
  function dist(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prev = [], cur, i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur = [i];
      var best = i;
      for (j = 1; j <= b.length; j++) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        if (cur[j] < best) best = cur[j];
      }
      if (best > max) return max + 1;
      prev = cur;
    }
    return prev[b.length];
  }

  // One typed word against one word of an entry.
  // "wil" -> "wilkins" (prefix); "009m2" -> "009" and "975xl2" -> "975xl" (a longer model
  // number still finds its family). Single characters must match exactly.
  function wordMatch(q, w) {
    if (q.length === 1) return q === w;
    if (w.indexOf(q) === 0) return true;
    return hasDigit(w) && w.length >= 3 && q.indexOf(w) === 0;
  }

  // Parse "handle: alias, alias" (brand aliases) and "words | Label | /url" (shortcuts).
  function splitList(s) {
    return String(s || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean);
  }
  function parsePages(text) {
    return String(text || '').split(/\r?\n/).map(function (line) {
      var p = line.split('|').map(function (x) { return x.trim(); });
      if (p.length < 3 || !p[1] || !/^(\/|https:\/\/)/.test(p[2])) return null;
      return { label: p[1], url: p[2], terms: splitList(p[0]).concat([p[1]]) };
    }).filter(Boolean);
  }
  function modelLabel(title) {
    return String(title || '').split(/\s+Backflow\b/i)[0].trim();
  }

  // Build the local index from the settings JSON and the model list.
  function buildIndex(config, models) {
    var entries = [];
    var brandAliases = {};
    (config.brands || []).forEach(function (b) {
      var terms = [b.l, b.h.replace(/-/g, ' ')].concat(splitList(b.a));
      // "Apollo / Conbraco" also answers to "apollo" and "conbraco" on Enter.
      b.l.split('/').forEach(function (part) { if (part.trim()) terms.push(part.trim()); });
      brandAliases[norm(b.l.split(/[\s/]/)[0])] = terms;
      entries.push({ type: 'brand', label: b.l, url: b.u, handle: b.h, terms: uniq(terms.map(norm)), enter: true });
    });

    var codeCount = {};
    var ms = (models || []).map(function (m) {
      var label = modelLabel(m.t);
      var ws = words(label);
      var brand = ws[0] || '';
      var codes = ws.slice(1).filter(hasDigit);
      codes.forEach(function (c) { codeCount[c] = (codeCount[c] || 0) + 1; });
      return { m: m, label: label, brand: brand, codes: codes };
    });
    ms.forEach(function (x) {
      var terms = [x.label];
      // Brand aliases carry over: "zurn 975xl" finds Wilkins 975XL, "apollo" finds Apollo models.
      (brandAliases[x.brand] || []).forEach(function (a) {
        if (norm(a) !== x.brand) terms.push(a + ' ' + x.codes.join(' '));
      });
      var enterCodes = [];
      x.codes.forEach(function (c) {
        terms.push(x.brand + ' ' + c);
        if (codeCount[c] === 1) { terms.push(c); enterCodes.push(c); }
      });
      entries.push({
        type: 'model', label: x.label, url: x.m.u, handle: x.m.h, count: x.m.n,
        terms: uniq(terms.map(norm)), enter: true
      });
    });

    parsePages(config.pages).forEach(function (p) {
      entries.push({ type: 'category', label: p.label, url: p.url, terms: uniq(p.terms.map(norm)), enter: true });
    });
    return entries;
  }

  function uniq(a) { var s = {}; return a.filter(function (x) { if (!x || s[x]) return false; s[x] = 1; return true; }); }

  // Score one entry: 100 exact, 80 starts with, 60 every word matches, 30 one typo.
  function scoreEntry(q, entry) {
    var qc = q.replace(/ /g, '');
    var qw = q.split(' ');
    var best = 0;
    for (var i = 0; i < entry.terms.length; i++) {
      var t = entry.terms[i];
      var tc = t.replace(/ /g, '');
      var tw = t.split(' ');
      var s = 0;
      if (qc === tc) s = 100;
      else if (qc.length >= 2 && tc.indexOf(qc) === 0) s = 80;
      else if (qw.every(function (w) { return tw.some(function (x) { return wordMatch(w, x); }); })) s = 60;
      else if (qw.length === 1 && qc.length >= 4 && !hasDigit(qc)) {
        var max = qc.length >= 7 ? 2 : 1;
        if (tw.some(function (x) { return x.length >= 4 && dist(qc, x, max) <= max; })) s = 30;
      }
      if (s > best) best = s;
      if (best === 100) break;
    }
    return best;
  }

  var LIMITS = { brand: 3, model: 6, category: 3 };

  // Local matches, grouped and sorted. Returns { brand: [...], model: [...], category: [...], exact }.
  function matchLocal(query, entries) {
    var q = norm(query);
    var out = { brand: [], model: [], category: [], exact: null };
    if (q.replace(/ /g, '').length < 2) return out;
    var scored = [];
    entries.forEach(function (e) {
      var s = scoreEntry(q, e);
      if (s > 0) scored.push({ e: e, s: s });
    });
    scored.sort(function (a, b) {
      return b.s - a.s || (b.e.count || 0) - (a.e.count || 0) || a.e.label.localeCompare(b.e.label);
    });
    scored.forEach(function (x) {
      if (out[x.e.type].length < LIMITS[x.e.type]) out[x.e.type].push(x.e);
      // Exact match wins Enter. Brand beats model beats category if two are exact.
      if (x.s === 100 && x.e.enter && !out.exact) out.exact = x.e;
    });
    return out;
  }

  // Merge predictive search results into the local groups.
  function mergeSuggest(local, json, config) {
    var res = (json && json.resources && json.resources.results) || {};
    var hideTags = splitList(config.hideTags).map(function (t) { return t.toLowerCase(); });
    var hideCols = splitList(config.hideCollections).map(function (t) { return t.toLowerCase(); });
    var limit = Math.max(1, Math.min(10, parseInt(config.productsLimit, 10) || 5));
    var seen = {};
    ['brand', 'model', 'category'].forEach(function (k) {
      local[k].forEach(function (e) { if (e.url) seen[e.url.split('?')[0]] = 1; });
    });
    var known = config._knownHandles || {};
    var cats = local.category.slice();
    (res.collections || []).forEach(function (c) {
      var path = String(c.url || '').split('?')[0];
      if (!c.handle || seen[path] || hideCols.indexOf(c.handle) > -1) return;
      if (cats.length >= LIMITS.category) return;
      seen[path] = 1;
      // A model or brand collection the local list didn't rank still belongs in its own group.
      var type = known[c.handle] || 'category';
      var e = { type: type, label: type === 'category' ? c.title : modelLabel(c.title), url: c.url };
      if (type === 'category') cats.push(e);
      else if (local[type].length < LIMITS[type]) local[type].push(e);
    });
    var products = (res.products || []).filter(function (p) {
      var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
      return !tags.some(function (t) { return hideTags.indexOf(t) > -1; });
    }).slice(0, limit).map(function (p) {
      var img = p.featured_image && p.featured_image.url;
      return { type: 'product', label: p.title, url: p.url, image: img ? sizedImage(img, 96) : '' };
    });
    return { brand: local.brand, model: local.model, category: cats, product: products, exact: local.exact };
  }

  function sizedImage(url, w) {
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  // What Enter does with nothing highlighted.
  function enterTarget(query, local, searchUrl) {
    if (local && local.exact) return local.exact.url;
    return searchUrl + '?q=' + encodeURIComponent(query.trim()) + '&options%5Bprefix%5D=last';
  }

  function suggestUrl(base, q, limit) {
    return base + '?q=' + encodeURIComponent(q) +
      '&resources%5Btype%5D=product%2Ccollection' +
      '&resources%5Blimit%5D=' + Math.min(10, limit + 4) +
      '&resources%5Blimit_scope%5D=each' +
      '&resources%5Boptions%5D%5Bunavailable_products%5D=last' +
      '&resources%5Boptions%5D%5Bfields%5D=title%2Cproduct_type%2Cvariants.title%2Cvariants.sku%2Cvendor%2Ctag';
  }

  var api = {
    norm: norm, compact: compact, dist: dist, wordMatch: wordMatch, parsePages: parsePages,
    modelLabel: modelLabel, buildIndex: buildIndex, scoreEntry: scoreEntry, matchLocal: matchLocal,
    mergeSuggest: mergeSuggest, enterTarget: enterTarget, suggestUrl: suggestUrl
  };

  /* ---------- browser ---------- */

  if (typeof document === 'undefined') return api;
  if (window.__bfdInstantSearch) return api; // bind once, even if the file is included twice
  window.__bfdInstantSearch = true;

  var INDEX_KEY = 'bfd_is_models_v1';
  var indexPromise = null;

  function loadModels(url) {
    if (indexPromise) return indexPromise;
    try {
      var cached = sessionStorage.getItem(INDEX_KEY);
      if (cached && cached !== '[]') { indexPromise = Promise.resolve(JSON.parse(cached)); return indexPromise; }
    } catch (e) { /* private mode */ }
    /* No "Accept: application/json" header here: with it, Shopify answers /collections with its
       own collections.json feed instead of the bfd-search-index view, so "models" came back
       undefined on the real store (found on the preview theme, 2026-10-09). */
    indexPromise = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (t) {
        var models = (JSON.parse(t).models) || [];
        /* Never keep an empty list for the session: one bad response would hide Models until the tab closes. */
        if (models.length) { try { sessionStorage.setItem(INDEX_KEY, JSON.stringify(models)); } catch (e) { /* full */ } }
        return models;
      })
      .catch(function () { indexPromise = null; return []; });
    return indexPromise;
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Bold the part of the label that matches what was typed.
  function mark(label, q) {
    var n = norm(q);
    if (!n) return esc(label);
    var out = esc(label);
    n.split(' ').filter(function (w) { return w.length > 1; }).forEach(function (w) {
      var re = new RegExp('(^|[^a-z0-9])(' + w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig');
      out = out.replace(re, '$1<mark>$2</mark>');
    });
    return out;
  }

  function Box(form) {
    this.form = form;
    this.input = form.querySelector('[data-bfd-is-input]');
    this.list = form.querySelector('[data-bfd-is-list]');
    this.status = form.querySelector('[data-bfd-is-status]');
    this.hint = form.querySelector('[data-bfd-is-hint]');
    /* One shared settings block for every box on the page (printed by the header through
       snippets/bfd-instant-search-config.liquid). "enabled": false leaves plain forms. */
    this.config = sharedConfig();
    if (!this.config || this.config.enabled === false || !this.input || !this.list) return;
    this.entries = buildIndex(this.config, []);
    this.cache = {};
    this.active = -1;
    this.options = [];
    this.timer = 0;
    this.ctrl = null;
    this.seq = 0;
    this.query = '';
    this.local = null;
    this.setup();
  }

  Box.prototype.setup = function () {
    var self = this, input = this.input;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', this.list.id);
    if (this.hint) { this.hint.hidden = false; input.setAttribute('aria-describedby', this.hint.id); }

    /* modelsReady: the model list has arrived (or failed) and this.entries includes it.
       onSubmit waits on warm() when Enter comes first, so a fast "009" + Enter on a slow
       connection still opens the model page instead of running a search. */
    this.modelsReady = false;
    this.modelsPromise = null;
    var warm = function () {
      if (self.modelsPromise) return self.modelsPromise;
      self.modelsPromise = loadModels(self.config.indexUrl).then(function (models) {
        self.entries = buildIndex(self.config, models);
        var known = {};
        self.entries.forEach(function (e) { if (e.handle) known[e.handle] = e.type; });
        self.config._knownHandles = known;
        self.modelsReady = true;
        if (self.query) self.renderLocal();
      });
      return self.modelsPromise;
    };
    this.warm = warm;
    input.addEventListener('focus', warm, { once: true });
    input.addEventListener('pointerenter', warm, { once: true });

    input.addEventListener('input', function () { self.onInput(); });
    input.addEventListener('keydown', function (e) { self.onKey(e); });
    input.addEventListener('focus', function () { if (self.query && self.options.length) self.open(); });
    this.list.addEventListener('mousedown', function (e) { e.preventDefault(); }); // keep focus in the box
    this.list.addEventListener('click', function (e) {
      var a = e.target.closest('[role="option"]');
      if (a && a.dataset.search) { e.preventDefault(); self.submitSearch(); }
    });
    document.addEventListener('pointerdown', function (e) { if (!self.form.contains(e.target)) self.close(); });
    this.form.addEventListener('submit', function (e) { self.onSubmit(e); });
    window.addEventListener('pageshow', function () { self.close(); });
  };

  Box.prototype.onInput = function () {
    var self = this;
    this.query = this.input.value;
    var q = norm(this.query);
    if (q.replace(/ /g, '').length < 2) { this.local = null; this.clear(); return; }
    this.renderLocal();
    clearTimeout(this.timer);
    // Shopify gets what was typed (sizes like 3/4" keep their slash); only the local match is normalized.
    var raw = rawKey(this.query);
    this.timer = setTimeout(function () { self.fetchProducts(raw); }, 150);
  };

  Box.prototype.renderLocal = function () {
    this.local = matchLocal(this.query, this.entries);
    var cached = this.cache[rawKey(this.query)];
    var data = cached ? mergeSuggest(cloneLocal(this.local), cached, this.config)
      : { brand: this.local.brand, model: this.local.model, category: this.local.category, product: null, exact: this.local.exact };
    this.render(data);
  };

  function rawKey(s) { return String(s).trim().toLowerCase().replace(/\s+/g, ' ').slice(0, 120); }

  function cloneLocal(l) {
    return { brand: l.brand.slice(), model: l.model.slice(), category: l.category.slice(), exact: l.exact };
  }

  Box.prototype.fetchProducts = function (q) {
    var self = this;
    if (this.cache[q]) { this.renderLocal(); return; }
    if (this.ctrl) this.ctrl.abort();
    this.ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var seq = ++this.seq;
    fetch(suggestUrl(this.config.suggestUrl, q, parseInt(this.config.productsLimit, 10) || 5),
      { credentials: 'same-origin', signal: this.ctrl ? this.ctrl.signal : undefined })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (json) {
        self.cache[q] = json;
        if (seq === self.seq && rawKey(self.input.value) === q) self.renderLocal();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        self.cache[q] = { resources: { results: {} } };
        if (seq === self.seq && rawKey(self.input.value) === q) self.renderLocal();
      });
  };

  Box.prototype.render = function (d) {
    var t = this.config.text, q = this.query, id = this.list.id, html = '', n = 0, self = this;
    var opt = function (e, inner, extra) {
      return '<a class="bfd-is__opt' + (extra || '') + '" role="option" aria-selected="false" tabindex="-1" id="' +
        id + '-o' + (n++) + '" href="' + esc(e.url) + '">' + inner + '</a>';
    };
    var group = function (key, title, items, fn) {
      if (!items || !items.length) return;
      html += '<div class="bfd-is__grp" role="group" aria-labelledby="' + id + '-g-' + key + '">' +
        '<div class="bfd-is__hdr" role="presentation" id="' + id + '-g-' + key + '">' + esc(title) + '</div>';
      items.forEach(function (e) { html += fn(e); });
      html += '</div>';
    };
    group('b', t.brands, d.brand, function (e) {
      return opt(e, '<span class="bfd-is__lbl">' + mark(e.label, q) + '</span><span class="bfd-is__tag"><span class="bfd-is__sr">, </span>' + esc(t.brandTag) + '</span>');
    });
    group('m', t.models, d.model, function (e) {
      var sub = e.count ? '<span class="bfd-is__sub"><span class="bfd-is__sr">, </span>' + esc(e.count + ' ' + t.parts) + '</span>' : '';
      return opt(e, '<span class="bfd-is__lbl">' + mark(e.label, q) + '</span>' + sub);
    });
    group('c', t.categories, d.category, function (e) {
      return opt(e, '<span class="bfd-is__lbl">' + mark(e.label, q) + '</span>');
    });
    if (d.product === null) {
      html += '<div class="bfd-is__grp" role="presentation"><div class="bfd-is__hdr" role="presentation">' + esc(t.products) +
        '</div><div class="bfd-is__wait" role="presentation">' + esc(t.loading) + '</div></div>';
    } else {
      group('p', t.products, d.product, function (e) {
        var img = e.image
          ? '<img src="' + esc(e.image) + '" alt="" width="40" height="40" loading="lazy" decoding="async">'
          : '<span class="bfd-is__noimg" aria-hidden="true"></span>';
        return opt(e, '<span class="bfd-is__img">' + img + '</span><span class="bfd-is__lbl">' + mark(e.label, q) + '</span>', ' bfd-is__opt--p');
      });
    }
    var count = n;
    if (!count && d.product !== null) {
      html += '<div class="bfd-is__none" role="presentation">' + esc(t.none) + '</div>';
    }
    html += opt({ url: this.config.searchUrl + '?q=' + encodeURIComponent(q.trim()) + '&options%5Bprefix%5D=last' },
      '<span class="bfd-is__lbl">' + esc(t.seeAll) + ' <strong>\u201C' + esc(q.trim()) + '\u201D</strong></span>', ' bfd-is__opt--all');
    this.list.innerHTML = html;
    var last = this.list.lastElementChild;
    if (last) last.dataset.search = '1';
    this.options = Array.prototype.slice.call(this.list.querySelectorAll('[role="option"]'));
    this.setActive(-1);
    this.open();
    if (d.product !== null) {
      clearTimeout(this.statusTimer);
      this.statusTimer = setTimeout(function () {
        self.status.textContent = count ? count + ' ' + t.count : t.none;
      }, 400);
    }
  };

  Box.prototype.open = function () {
    if (!this.options.length) return;
    this.list.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.fit();
  };
  Box.prototype.close = function () {
    this.list.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.setActive(-1);
  };
  Box.prototype.clear = function () {
    this.list.innerHTML = '';
    this.options = [];
    clearTimeout(this.statusTimer); // or a pending "No quick matches" lands after the box is emptied
    this.status.textContent = '';
    this.close();
  };

  // Keep the list inside the visible screen (phone keyboards take half of it).
  Box.prototype.fit = function () {
    var vv = window.visualViewport;
    var h = vv ? vv.height : window.innerHeight;
    var top = this.list.parentNode.getBoundingClientRect().bottom;
    var room = Math.max(220, Math.min(520, h - top - 12));
    this.list.style.maxHeight = room + 'px';
  };

  Box.prototype.setActive = function (i) {
    if (this.options[this.active]) this.options[this.active].setAttribute('aria-selected', 'false');
    this.active = i;
    var el = this.options[i];
    if (el) {
      el.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', el.id);
      el.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  };

  Box.prototype.onKey = function (e) {
    var n = this.options.length;
    var openNow = !this.list.hidden;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!openNow) { this.open(); if (e.altKey) return; }
        if (n) this.setActive(this.active + 1 >= n ? 0 : this.active + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!openNow) this.open();
        if (n) this.setActive(this.active - 1 < 0 ? n - 1 : this.active - 1);
        break;
      case 'Escape':
        if (openNow) { e.preventDefault(); this.close(); }
        else if (this.input.value) { e.preventDefault(); this.input.value = ''; this.query = ''; this.clear(); }
        break;
      case 'Tab':
        this.close();
        break;
    }
  };

  Box.prototype.onSubmit = function (e) {
    var q = this.input.value.trim();
    if (!q) { e.preventDefault(); this.input.focus(); return; }
    var el = !this.list.hidden && this.options[this.active];
    if (el) {
      e.preventDefault();
      if (el.dataset.search) this.submitSearch(); else window.location.href = el.href;
      return;
    }
    var local = this.local && norm(this.query) === norm(q) ? this.local : matchLocal(q, this.entries);
    if (local.exact) { e.preventDefault(); window.location.href = local.exact.url; return; }
    if (this.modelsReady) return; // nothing exact: let the form submit normally to /search
    // The model list is still loading. Wait for it (1 second at most), then decide.
    e.preventDefault();
    var self = this;
    var decided = false;
    var decide = function () {
      if (decided) return;
      decided = true;
      var again = matchLocal(q, self.entries);
      if (again.exact) window.location.href = again.exact.url; else self.submitSearch();
    };
    this.warm().then(decide, decide);
    setTimeout(decide, 1000);
  };

  Box.prototype.submitSearch = function () {
    this.close();
    window.location.href = enterTarget(this.input.value, null, this.config.searchUrl);
  };

  var shared;
  function sharedConfig() {
    if (shared !== undefined) return shared;
    var el = document.getElementById('bfd-is-config');
    try { shared = JSON.parse(el.textContent); } catch (e) { shared = null; }
    return shared;
  }

  /* Homepage: the header has no search box of its own there, so its search icon
     ([data-bfd-is-jump]) puts the cursor in the hero box instead. focus() runs inside the
     tap, which is what makes phones open the keyboard. Without JavaScript the icon is a
     plain link to /search. */
  document.addEventListener('click', function (e) {
    var jump = e.target.closest && e.target.closest('[data-bfd-is-jump]');
    if (!jump) return;
    var input = document.querySelector('form[data-bfd-is-hero] [data-bfd-is-input]');
    if (!input) return;
    e.preventDefault();
    input.focus({ preventScroll: true });
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    input.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
  });

  function init(root) {
    (root || document).querySelectorAll('form[data-bfd-is]').forEach(function (f) {
      if (f.__bfdIs) return;
      f.__bfdIs = new Box(f);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  return api;
});
